import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Divider,
    IconButton,
    Tooltip
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import StorageIcon from '@material-ui/icons/Storage';
import MemoryIcon from '@material-ui/icons/Memory';
import SpeedIcon from '@material-ui/icons/Speed';
import SecurityIcon from '@material-ui/icons/Security';

const useStyles = makeStyles((theme) => ({
    dialogPaper: {
        minWidth: '800px',
        maxWidth: '1000px',
        maxHeight: '85vh'
    },
    dialogTitle: {
        borderBottom: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(2, 3),
        backgroundColor: theme.palette.background.default,
        position: 'relative'
    },
    titleContent: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        paddingRight: theme.spacing(5) // Space for close button
    },
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500]
    },
    dialogContent: {
        padding: theme.spacing(3)
    },
    policyCard: {
        marginBottom: theme.spacing(3),
        padding: theme.spacing(2.5),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
        transition: 'box-shadow 0.3s ease',
        '&:hover': {
            boxShadow: theme.shadows[4]
        }
    },
    policyHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: theme.spacing(2)
    },
    policyTitle: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5)
    },
    policyName: {
        fontWeight: 600,
        fontSize: '1.1rem',
        textTransform: 'capitalize'
    },
    sectionTitle: {
        fontWeight: 600,
        fontSize: '0.9rem',
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(1),
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        color: theme.palette.text.primary
    },
    tableContainer: {
        marginTop: theme.spacing(1.5),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(0.5)
    },
    tableHeader: {
        backgroundColor: theme.palette.background.default,
        fontWeight: 600
    },
    resiliencyBox: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        padding: theme.spacing(1.5),
        backgroundColor: theme.palette.background.default,
        borderRadius: theme.spacing(0.5),
        marginTop: theme.spacing(1)
    },
    resiliencyItem: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5)
    },
    chip: {
        fontWeight: 500
    },
    noData: {
        padding: theme.spacing(3),
        textAlign: 'center',
        color: theme.palette.text.secondary
    }
}));

const PolicyDetailsDialog = ({ open, onClose, policies, providerName }) => {
    const classes = useStyles();

    if (!policies || policies.length === 0) {
        return (
            <Dialog open={open} onClose={onClose} classes={{ paper: classes.dialogPaper }}>
                <DialogTitle className={classes.dialogTitle}>
                    <Box className={classes.titleContent}>
                        <Typography variant="h6">Storage Policies</Typography>
                    </Box>
                    <IconButton size="small" onClick={onClose} className={classes.closeButton}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent className={classes.dialogContent}>
                    <Box className={classes.noData}>
                        <Typography variant="body1">
                            No storage policies available for this provider.
                        </Typography>
                    </Box>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog 
            open={open} 
            onClose={onClose} 
            maxWidth={false}
            classes={{ paper: classes.dialogPaper }}
        >
            <DialogTitle className={classes.dialogTitle}>
                <Box className={classes.titleContent}>
                    <Box>
                        <Typography variant="h6">
                            Storage Policies - {providerName}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                            Available configurations and constraints
                        </Typography>
                    </Box>
                </Box>
                <IconButton size="small" onClick={onClose} className={classes.closeButton}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent className={classes.dialogContent}>
                {policies.map((policy, index) => (
                    <Paper key={policy.name || index} className={classes.policyCard} elevation={0}>
                        {/* Policy Header */}
                        <Box className={classes.policyHeader}>
                            <Box className={classes.policyTitle}>
                                <Typography className={classes.policyName}>
                                    {policy.name}
                                </Typography>
                                <Chip 
                                    label={`${policy.allowedConfigurations?.length || 0} Configurations`}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    className={classes.chip}
                                />
                            </Box>
                        </Box>

                        {/* Resiliency Configuration */}
                        <Box>
                            <Typography className={classes.sectionTitle}>
                                <SecurityIcon fontSize="small" color="action" />
                                {policy.name === 'custom' ? 'Custom Configuration' : 'Resiliency Configuration'}
                            </Typography>
                            {policy.name === 'custom' ? (
                                <Box className={classes.resiliencyBox}>
                                    <Typography variant="body2" color="textSecondary">
                                        Custom policy allows you to define your own storage configuration with flexible instance types, 
                                        node counts, and volume sizes. You have full control over the infrastructure setup without 
                                        predefined resiliency constraints.
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                                        <strong>Note:</strong> Incorrect configurations may cause deployment failures. 
                                        Use predefined policies for guaranteed compatibility.
                                    </Typography>
                                </Box>
                            ) : (
                                <Box className={classes.resiliencyBox}>
                                    <Box className={classes.resiliencyItem}>
                                        <Typography variant="body2" color="textSecondary">
                                            Media Failures:
                                        </Typography>
                                        <Chip 
                                            label={policy.resiliencymedia ?? 'N/A'} 
                                            size="small" 
                                            color="primary"
                                            variant="outlined"
                                        />
                                    </Box>
                                    <Box className={classes.resiliencyItem}>
                                        <Typography variant="body2" color="textSecondary">
                                            Node Failures:
                                        </Typography>
                                        <Chip 
                                            label={policy.resiliencynode ?? 'N/A'} 
                                            size="small" 
                                            color="primary"
                                            variant="outlined"
                                        />
                                    </Box>
                                    <Box className={classes.resiliencyItem}>
                                        <Typography variant="body2" color="textSecondary">
                                            Zone Failures:
                                        </Typography>
                                        <Chip 
                                            label={policy.resiliencyzone ?? 'N/A'} 
                                            size="small" 
                                            color="primary"
                                            variant="outlined"
                                        />
                                    </Box>
                                </Box>
                            )}
                        </Box>

                        {/* Allowed Configurations */}
                        {policy.allowedConfigurations && policy.allowedConfigurations.length > 0 && (
                            <Box>
                                <Typography className={classes.sectionTitle}>
                                    <MemoryIcon fontSize="small" color="action" />
                                    Allowed Instance Configurations
                                </Typography>
                                <TableContainer className={classes.tableContainer}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell className={classes.tableHeader}>Instance Type</TableCell>
                                                <TableCell className={classes.tableHeader} align="center">Node Count</TableCell>
                                                <TableCell className={classes.tableHeader} align="center">Max Volume Size</TableCell>
                                                <TableCell className={classes.tableHeader}>Description</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {policy.allowedConfigurations.map((config, idx) => (
                                                <TableRow key={idx} hover>
                                                    <TableCell>
                                                        <Chip 
                                                            label={config.instanceType}
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Typography variant="body2">
                                                            {config.nodeCount}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Typography variant="body2">
                                                            {config.maxVolumeSize} GB
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" color="textSecondary">
                                                            {config.description || '-'}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        )}

                        {/* Allowed Volume Sizes */}
                        {policy.allowedConfigurations?.[0]?.allowedVolumeSizes && 
                         policy.allowedConfigurations[0].allowedVolumeSizes.length > 0 && (
                            <Box mt={2}>
                                <Typography className={classes.sectionTitle}>
                                    <SpeedIcon fontSize="small" color="action" />
                                    Allowed Volume Sizes
                                </Typography>
                                <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                                    {policy.allowedConfigurations[0].allowedVolumeSizes.map((size, idx) => (
                                        <Chip 
                                            key={idx}
                                            label={`${size} GB`}
                                            size="small"
                                            variant="outlined"
                                        />
                                    ))}
                                </Box>
                            </Box>
                        )}

                        {index < policies.length - 1 && (
                            <Divider style={{ marginTop: 16 }} />
                        )}
                    </Paper>
                ))}
            </DialogContent>

            <DialogActions style={{ padding: '16px 24px', borderTop: '1px solid rgba(0, 0, 0, 0.12)' }}>
                <Button onClick={onClose} color="primary" variant="contained">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PolicyDetailsDialog;
